import { useState, useCallback, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Ticket, Share2, X, Sparkles, Clock, Cake, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toPng } from 'html-to-image'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'
import { liff } from '../lib/liff'
import { HoroscopeShareCard } from '../components/HoroscopeShareCard'

interface HoroscopeData {
  date?: string
  career?: string | null
  finance?: string | null
  love?: string | null
  health?: string | null
  lucky_numbers?: number[]
  lucky_color?: string
  work_color?: string
  love_color?: string
  finance_color?: string
  health_color?: string
  bad_luck_color?: string
  overall_score?: number
  meta?: {
    needs_birth_date?: boolean
    birth_date?: string | null
    started: boolean
    free_used: boolean
    unlocked_today: string[]
    tickets: number
    next_refresh?: string
  }
}

const T = {
  title:        '\u0E14\u0E39\u0E14\u0E27\u0E07\u0E23\u0E32\u0E22\u0E27\u0E31\u0E19',
  loadingMain:  '\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49...',
  loadingSub:   '\u0E01\u0E23\u0E38\u0E13\u0E32\u0E23\u0E2D\u0E0B\u0E31\u0E01\u0E04\u0E23\u0E39\u0E48',
  startTitle:   '\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  startDesc:    '\u0E14\u0E39\u0E04\u0E30\u0E41\u0E19\u0E19\u0E14\u0E27\u0E07 \u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25 \u0E2A\u0E35\u0E21\u0E07\u0E04\u0E25 \u0E1F\u0E23\u0E35\u0E17\u0E38\u0E01\u0E27\u0E31\u0E19',
  startBtn:     '\u0E40\u0E23\u0E34\u0E48\u0E21\u0E14\u0E39\u0E14\u0E27\u0E07',
  startNote:    '\u0E14\u0E39\u0E14\u0E27\u0E07\u0E1F\u0E23\u0E35\u0E17\u0E38\u0E01\u0E2B\u0E21\u0E27\u0E14 \u0E17\u0E38\u0E01\u0E27\u0E31\u0E19 \u2728',
  freePrefix:   '\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07\u0E1F\u0E23\u0E35 ',
  scoreTitle:   '\u0E04\u0E30\u0E41\u0E19\u0E19\u0E14\u0E27\u0E07\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  luckyNum:     '\u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25',
  luckyColor:   '\u0E2A\u0E35\u0E21\u0E07\u0E04\u0E25',
  career:       '\u0E01\u0E32\u0E23\u0E07\u0E32\u0E19',
  finance:      '\u0E01\u0E32\u0E23\u0E40\u0E07\u0E34\u0E19',
  love:         '\u0E04\u0E27\u0E32\u0E21\u0E23\u0E31\u0E01',
  health:       '\u0E2A\u0E38\u0E02\u0E20\u0E32\u0E1E',
  openPrefix:   '\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E39\u0E14\u0E27\u0E07',
  confirmTitle: '\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07',
  useFree:      '\u0E43\u0E0A\u0E49\u0E2A\u0E34\u0E17\u0E18\u0E34\u0E4C\u0E1F\u0E23\u0E35\u0E1B\u0E23\u0E30\u0E08\u0E33\u0E27\u0E31\u0E19 \u0E40\u0E1B\u0E34\u0E14\u0E14\u0E39\u0E14\u0E27\u0E07',
  freeSuffix:   '\u0E44\u0E14\u0E49\u0E40\u0E25\u0E22',
  freeUsedMsg:  '\u0E43\u0E0A\u0E49\u0E2A\u0E34\u0E17\u0E18\u0E34\u0E4C\u0E1F\u0E23\u0E35\u0E44\u0E1B\u0E41\u0E25\u0E49\u0E27\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  useTicket:    '\u0E43\u0E0A\u0E49 1 \u0E15\u0E31\u0E4B\u0E27',
  remaining:    '\u0E40\u0E2B\u0E25\u0E37\u0E2D',
  tickets:      '\u0E43\u0E1A',
  buyPrompt:    '\u0E01\u0E23\u0E38\u0E13\u0E32\u0E0B\u0E37\u0E49\u0E2D\u0E15\u0E31\u0E4B\u0E27\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07',
  buyHint:      '\u0E40\u0E23\u0E34\u0E48\u0E21\u0E15\u0E49\u0E19 9\u0E3F = 1 \u0E15\u0E31\u0E4B\u0E27',
  cancel:       '\u0E22\u0E01\u0E40\u0E25\u0E34\u0E01',
  buy:          '\u0E0B\u0E37\u0E49\u0E2D\u0E15\u0E31\u0E4B\u0E27',
  opening:      '\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E34\u0E14...',
  confirm:      '\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19',
  noTickets:    '\u0E44\u0E21\u0E48\u0E21\u0E35\u0E15\u0E31\u0E4B\u0E27\u0E40\u0E1E\u0E35\u0E22\u0E07\u0E1E\u0E2D \u0E01\u0E23\u0E38\u0E13\u0E32\u0E0B\u0E37\u0E49\u0E2D\u0E15\u0E31\u0E4B\u0E27\u0E01\u0E48\u0E2D\u0E19',
  errorGeneral: '\u0E40\u0E01\u0E34\u0E14\u0E02\u0E49\u0E2D\u0E1C\u0E34\u0E14\u0E1E\u0E25\u0E32\u0E14 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48',
  errorLoad:    '\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E42\u0E2B\u0E25\u0E14\u0E14\u0E27\u0E07\u0E44\u0E14\u0E49 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48',
  retry:        '\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48',
  shareToday:   '\u0E41\u0E0A\u0E23\u0E4C\u0E14\u0E27\u0E07\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  copied:       '\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01\u0E41\u0E25\u0E49\u0E27! \u0E27\u0E32\u0E07\u0E43\u0E19\u0E41\u0E2D\u0E1B\u0E44\u0E14\u0E49\u0E40\u0E25\u0E22',
  disclaimer:   '\u0E04\u0E33\u0E17\u0E33\u0E19\u0E32\u0E22\u0E21\u0E35\u0E44\u0E27\u0E49\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E04\u0E27\u0E32\u0E21\u0E1A\u0E31\u0E19\u0E40\u0E17\u0E34\u0E07\u0E40\u0E17\u0E48\u0E32\u0E19\u0E31\u0E49\u0E19',
  shareDate:    '\u0E14\u0E27\u0E07\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  shareScore:   '\u0E04\u0E30\u0E41\u0E19\u0E19',
  shareApp:     '\u0E14\u0E39\u0E14\u0E27\u0E07\u0E1F\u0E23\u0E35\u0E17\u0E35\u0E48 Amulet Digit',
  alreadyTitle: '\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07\u0E44\u0E1B\u0E41\u0E25\u0E49\u0E27\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49',
  alreadyDesc:  '\u0E14\u0E27\u0E07\u0E08\u0E30\u0E23\u0E35\u0E40\u0E1F\u0E23\u0E0A\u0E27\u0E31\u0E19\u0E43\u0E2B\u0E21\u0E48',
  ok:           '\u0E15\u0E01\u0E25\u0E07',
  refreshAt:    '\u0E23\u0E35\u0E40\u0E1F\u0E23\u0E0A',
  nextRefresh:  '\u0E23\u0E35\u0E40\u0E1F\u0E23\u0E0A\u0E14\u0E27\u0E07\u0E43\u0E2B\u0E21\u0E48',
  guideNote:    '\u0E14\u0E39\u0E14\u0E27\u0E07 4 \u0E14\u0E49\u0E32\u0E19 + \u0E2A\u0E35\u0E21\u0E07\u0E04\u0E25 6 \u0E2A\u0E35 \u0E1B\u0E23\u0E30\u0E08\u0E33\u0E27\u0E31\u0E19',
}

const COLOR_MAP: Record<string, string> = {
  '\u0E41\u0E14\u0E07':           '#e53e3e',
  '\u0E2A\u0E49\u0E21':           '#ed8936',
  '\u0E40\u0E2B\u0E25\u0E37\u0E2D\u0E07': '#d69e2e',
  '\u0E17\u0E2D\u0E07':           '#b8945a',
  '\u0E40\u0E02\u0E35\u0E22\u0E27': '#38a169',
  '\u0E19\u0E49\u0E33\u0E40\u0E07\u0E34\u0E19': '#3182ce',
  '\u0E1F\u0E49\u0E32':           '#63b3ed',
  '\u0E21\u0E48\u0E27\u0E07':     '#805ad5',
  '\u0E0A\u0E21\u0E1E\u0E39':     '#d53f8c',
  '\u0E02\u0E32\u0E27':           '#f7fafc',
  '\u0E14\u0E33':                 '#2d3748',
  '\u0E40\u0E17\u0E32':           '#718096',
  '\u0E19\u0E49\u0E33\u0E15\u0E32\u0E25': '#975a16',
  '\u0E04\u0E23\u0E35\u0E21':     '#fefcbf',
}

function getColorHex(name: string): string {
  for (const [k, v] of Object.entries(COLOR_MAP)) {
    if (name.includes(k)) return v
  }
  return '#b8945a'
}

function formatThaiDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const days = ['\u0E2D\u0E32\u0E17\u0E34\u0E15\u0E22\u0E4C','\u0E08\u0E31\u0E19\u0E17\u0E23\u0E4C','\u0E2D\u0E31\u0E07\u0E04\u0E32\u0E23','\u0E1E\u0E38\u0E18','\u0E1E\u0E24\u0E2B\u0E31\u0E2A','\u0E28\u0E38\u0E01\u0E23\u0E4C','\u0E40\u0E2A\u0E32\u0E23\u0E4C']
  const months = ['\u0E21.\u0E04.','\u0E01.\u0E1E.','\u0E21\u0E35.\u0E04.','\u0E40\u0E21.\u0E22.','\u0E1E.\u0E04.','\u0E21\u0E34.\u0E22.','\u0E01.\u0E04.','\u0E2A.\u0E04.','\u0E01.\u0E22.','\u0E15.\u0E04.','\u0E1E.\u0E22.','\u0E18.\u0E04.']
  return `\u0E27\u0E31\u0E19${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
}

function ScoreBar({ score }: { score: number }) {
  const pct = (score / 10) * 100
  const color = score >= 8 ? '#38a169' : score >= 6 ? '#b8945a' : '#e53e3e'
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-surface-3)' }}>
      <div className="h-full rounded-full transition-all duration-1000"
           style={{ width: `${pct}%`, background: color }} />
    </div>
  )
}

function FullLoadingScreen({ subText }: { subText?: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-8">
      <div className="relative flex items-center justify-center">
        <div className="absolute w-32 h-32 rounded-full animate-ping opacity-20"
             style={{ background: 'var(--gradient-gold)' }} />
        <div className="absolute w-24 h-24 rounded-full animate-ping opacity-30"
             style={{ background: 'var(--gradient-gold)', animationDelay: '0.3s' }} />
        <div className="relative w-20 h-20 rounded-full flex items-center justify-center shadow-2xl"
             style={{ background: 'var(--gradient-gold)', boxShadow: '0 0 40px rgba(184,148,90,0.5)' }}>
          <Sparkles size={36} color="#fff" />
        </div>
      </div>
      <div className="text-center space-y-2">
        <p className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>{T.loadingMain}</p>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{subText ?? T.loadingSub}</p>
      </div>
      <div className="flex gap-2">
        {[0, 1, 2].map(i => (
          <div key={i} className="w-2 h-2 rounded-full animate-bounce"
               style={{ background: 'var(--color-gold-dark)', animationDelay: `${i * 0.2}s`, opacity: 0.7 }} />
        ))}
      </div>
    </div>
  )
}

// Birth date form — รับวันเกิด แล้ว save ลง user
const BD = {
  title:    'ใส่วันเกิดของคุณ',
  desc:     'หมอดูจะอ่านดวงให้เฉพาะคุณ จากปีนักษัตรและวันเกิด',
  label:    'วันเกิด',
  save:     'บันทึก',
  saving:   'กำลังบันทึก...',
  invalid:  'วันเกิดไม่ถูกต้อง',
  hint:     'จะจำค่านี้ไว้ — แก้ได้ภายหลังที่หน้าโปรไฟล์',
}

function BirthDateForm({ onSave, loading, error, initial }: {
  onSave: (date: string) => void
  loading: boolean
  error: string | null
  initial?: string | null
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(initial ?? '')
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today && date >= '1920-01-01'

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-8 text-center">
      <div className="relative">
        <div className="absolute inset-0 rounded-full blur-2xl opacity-50"
             style={{ background: 'var(--gradient-gold)' }} />
        <div className="relative w-24 h-24 rounded-full flex items-center justify-center"
             style={{ background: 'var(--gradient-gold)', boxShadow: '0 8px 32px rgba(184,148,90,0.4)' }}>
          <Cake size={44} color="#fff" />
        </div>
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>{BD.title}</h2>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{BD.desc}</p>
      </div>
      <div className="w-full max-w-xs flex flex-col gap-3">
        <label className="text-xs font-semibold text-left" style={{ color: 'var(--color-gold-dark)' }}>
          {BD.label}
        </label>
        <input
          type="date"
          value={date}
          max={today}
          min="1920-01-01"
          onChange={e => setDate(e.target.value)}
          className="w-full px-4 py-3 rounded-2xl text-base outline-none"
          style={{
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-surface-3)',
          }}
        />
        {error && <p className="text-xs text-left" style={{ color: '#e53e3e' }}>{error}</p>}
        <button
          onClick={() => valid && onSave(date)}
          disabled={!valid || loading}
          className="w-full py-4 rounded-2xl font-semibold btn-gold shimmer disabled:opacity-40"
        >
          {loading ? BD.saving : BD.save}
        </button>
      </div>
      <p className="text-xs" style={{ color: 'var(--color-text-soft)' }}>{BD.hint}</p>
    </div>
  )
}

function EmptyState({ onStart, loading }: { onStart: () => void; loading: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-8 text-center">
      <div className="relative">
        <div className="absolute inset-0 rounded-full blur-2xl opacity-50"
             style={{ background: 'var(--gradient-gold)' }} />
        <div className="relative w-24 h-24 rounded-full flex items-center justify-center"
             style={{ background: 'var(--gradient-gold)', boxShadow: '0 8px 32px rgba(184,148,90,0.4)' }}>
          <Sparkles size={44} color="#fff" />
        </div>
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>{T.startTitle}</h2>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{T.startDesc}</p>
      </div>
      <button
        onClick={onStart}
        disabled={loading}
        className="w-full max-w-xs py-5 rounded-2xl text-lg disabled:opacity-50 select-none btn-gold-3d"
        onPointerDown={e => {
          if (loading) return
          const el = e.currentTarget
          el.style.transform = 'translateY(5px)'
          el.style.boxShadow = '0 1px 0 #7a5820, 0 4px 12px rgba(184,148,90,0.3)'
          el.style.animation = 'none'
        }}
        onPointerUp={e => {
          const el = e.currentTarget
          el.style.transform = ''
          el.style.boxShadow = ''
          el.style.animation = ''
        }}
        onPointerLeave={e => {
          const el = e.currentTarget
          el.style.transform = ''
          el.style.boxShadow = ''
          el.style.animation = ''
        }}
      >
        {loading ? T.loadingMain : '✨ เริ่มดูดวงฟรี ✨'}
      </button>
      <p className="text-xs" style={{ color: 'var(--color-text-soft)' }}>{T.startNote}</p>
    </div>
  )
}

function AlreadyStartedModal({ onClose, nextRefresh }: { onClose: () => void; nextRefresh?: string }) {
  const refreshTime = nextRefresh
    ? new Date(nextRefresh).toLocaleString('th-TH', {
        hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short',
        timeZone: 'Asia/Bangkok',
      })
    : '00:00'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl p-6 text-center"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-center mb-3">
          <Clock size={32} color="#b8945a" />
        </div>
        <h3 className="text-base font-bold mb-1" style={{ color: 'var(--color-text)' }}>{T.alreadyTitle}</h3>
        <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>{T.alreadyDesc}</p>
        <p className="text-xs mb-5" style={{ color: 'var(--color-gold-dark)' }}>
          {T.refreshAt}: {refreshTime}
        </p>
        <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold btn-gold">
          {T.ok}
        </button>
      </div>
    </div>
  )
}

// ── Social media SVG logos ────────────────────────────────
function FbLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <rect width="32" height="32" rx="8" fill="#1877F2"/>
      <path d="M21 10h-3c-.6 0-1 .4-1 1v2h4l-.5 3H17v8h-3v-8h-2v-3h2v-2c0-2.8 1.7-4 4.3-4H21v3z" fill="white"/>
    </svg>
  )
}
function IgLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <defs>
        <linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F58529"/>
          <stop offset="40%" stopColor="#DD2A7B"/>
          <stop offset="80%" stopColor="#8134AF"/>
          <stop offset="100%" stopColor="#515BD4"/>
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#ig)"/>
      <rect x="7" y="7" width="18" height="18" rx="5" stroke="white" strokeWidth="1.8" fill="none"/>
      <circle cx="16" cy="16" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
      <circle cx="22" cy="10" r="1.3" fill="white"/>
    </svg>
  )
}
function LineLogo() {
  return (
    <svg viewBox="0 0 32 32" width="30" height="30">
      <rect width="32" height="32" rx="8" fill="#06C755"/>
      <path d="M26 14.8C26 9.9 21.1 6 15 6S4 9.9 4 14.8c0 4.4 3.9 8 9.2 8.7.4.1.9.3 1 .6.1.3.1.8 0 1.1l-.2 1c0 .3-.2 1.1 1 .6C16.3 26 22 22.3 24.5 19.4 26.2 17.7 26 16.3 26 14.8z" fill="white"/>
      <path d="M13.1 12.8H12c-.2 0-.3.1-.3.3v4c0 .2.1.3.3.3h1.1c.2 0 .3-.1.3-.3v-4c0-.2-.1-.3-.3-.3zM20 12.8h-1.1c-.2 0-.3.1-.3.3v2.4l-1.9-2.5c-.1-.1-.2-.2-.3-.2H15.3c-.2 0-.3.1-.3.3v4c0 .2.1.3.3.3h1.1c.2 0 .3-.1.3-.3v-2.4l1.9 2.5c.1.1.2.2.3.2H20c.2 0 .3-.1.3-.3v-4c0-.2-.1-.3-.3-.3zM11 15.7H9.4v-2.6c0-.2-.1-.3-.3-.3H8c-.2 0-.3.1-.3.3v4c0 .1 0 .2.1.2h.1c0 .1.1.1.2.1H11c.2 0 .3-.1.3-.3v-1.1c0-.2-.1-.3-.3-.3zM24 13.9c.2 0 .3-.1.3-.3v-1.1c0-.2-.1-.3-.3-.3h-3c-.1 0-.2 0-.2.1h-.1c-.1.1-.1.1-.1.2v4c0 .1 0 .2.1.2h.1c0 .1.1.1.2.1h3c.2 0 .3-.1.3-.3v-1.1c0-.2-.1-.3-.3-.3h-1.9v-.6H24c.2 0 .3-.1.3-.3v-1c0-.2-.1-.3-.3-.3h-1.9v-.6H24z" fill="#06C755"/>
    </svg>
  )
}

function ShareImageModal({ data, onClose }: { data: HoroscopeData; onClose: () => void }) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [imgUrl, setImgUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [lineSharing, setLineSharing] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  useEffect(() => {
    // Wait one tick for the card to render before capturing
    const timer = setTimeout(async () => {
      if (!cardRef.current) return
      try {
        const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true })
        setImgUrl(dataUrl)
      } catch {
        setImgUrl(null)
      } finally {
        setGenerating(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [])

  const getFile = async (): Promise<File | null> => {
    if (!imgUrl) return null
    const res = await fetch(imgUrl)
    const blob = await res.blob()
    return new File([blob], `horoscope-${data.date ?? 'today'}.png`, { type: 'image/png' })
  }

  const handleNativeShare = async () => {
    setSharing(true)
    try {
      const file = await getFile()
      if (!file) { showToast('ยังสร้างภาพไม่เสร็จ'); return }
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `ดวงวันนี้ ${data.overall_score}/10 ✨`,
          text: `ดูดวงฟรีทุกวัน ที่ Amulet Digit\nhttps://www.amuletdigit.com`,
        })
      } else {
        // Fallback: open native share without files (text only)
        await navigator.share({
          title: `ดวงวันนี้ ${data.overall_score}/10 ✨`,
          text: `ดูดวงฟรีทุกวัน ที่ Amulet Digit`,
          url: 'https://www.amuletdigit.com',
        }).catch(() => handleDownload())
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name !== 'AbortError') showToast('แชร์ไม่สำเร็จ')
    } finally {
      setSharing(false)
    }
  }

  const handleLineShare = async () => {
    setLineSharing(true)
    try {
      if (!liff.isInClient()) {
        showToast('ใช้ได้เฉพาะใน LINE เท่านั้น')
        return
      }
      const score = data.overall_score ?? 0
      const dateStr = data.date ? formatThaiDate(data.date) : ''
      const scoreColor = score >= 8 ? '#3daa66' : score >= 6 ? '#b8945a' : '#d47670'
      const mood = score >= 8 ? '✨ ดีมาก โชคหนุนนำ' : score >= 6 ? '🌟 ดี มีโอกาสรออยู่' : '⚡ ระมัดระวัง ใช้สติ'
      const liffUrl = 'https://liff.line.me/2009976171-SBRuplHE'

      const result = await liff.shareTargetPicker(
        [{
          type: 'flex',
          altText: `ดวงวันนี้ ${score}/10 ✨ — ดูดวงฟรีที่ Amulet Digit`,
          contents: {
            type: 'bubble',
            header: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#16120d', paddingAll: '16px',
              contents: [
                { type: 'text', text: '🪬 Amulet Digit', color: '#d4b884', weight: 'bold', size: 'xl', align: 'center' },
                { type: 'text', text: 'ดูดวงฟรีทุกวัน • ทำนายฝัน • เลขมงคล', color: '#7a7260', size: 'xxs', align: 'center', margin: 'xs' },
              ],
            },
            body: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#1c1810', paddingAll: '16px', spacing: 'sm',
              contents: [
                { type: 'text', text: dateStr, color: '#b8945a', size: 'sm', align: 'center', weight: 'bold' },
                {
                  type: 'box', layout: 'horizontal', margin: 'md',
                  contents: [
                    { type: 'text', text: 'คะแนนดวงวันนี้', color: '#b8b09a', size: 'sm', flex: 1, gravity: 'center' },
                    { type: 'text', text: `${score}/10`, color: scoreColor, size: 'xxl', weight: 'bold', align: 'end' },
                  ],
                },
                { type: 'text', text: mood, color: '#b8b09a', size: 'xs', align: 'center' },
                { type: 'separator', margin: 'md', color: '#2d2215' },
                { type: 'text', text: '⚜️ การงาน', color: '#c49030', size: 'xs', weight: 'bold', margin: 'md' },
                { type: 'text', text: data.career ?? '', color: '#b8b09a', size: 'xs', wrap: true },
                { type: 'text', text: '💎 การเงิน', color: '#9f7ae8', size: 'xs', weight: 'bold', margin: 'sm' },
                { type: 'text', text: data.finance ?? '', color: '#b8b09a', size: 'xs', wrap: true },
                { type: 'text', text: '🌹 ความรัก', color: '#e05575', size: 'xs', weight: 'bold', margin: 'sm' },
                { type: 'text', text: data.love ?? '', color: '#b8b09a', size: 'xs', wrap: true },
                { type: 'text', text: '🌿 สุขภาพ', color: '#3daa66', size: 'xs', weight: 'bold', margin: 'sm' },
                { type: 'text', text: data.health ?? '', color: '#b8b09a', size: 'xs', wrap: true },
                { type: 'separator', margin: 'md', color: '#2d2215' },
                { type: 'text', text: `สีมงคล: ${data.lucky_color ?? '—'} • สีกาลกิณี: ${data.bad_luck_color ?? '—'}`, color: '#7a7260', size: 'xxs', align: 'center', margin: 'sm' },
              ],
            },
            footer: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#16120d', paddingAll: '12px',
              contents: [{
                type: 'button',
                action: { type: 'uri', label: 'ดูดวงฟรีเลย ✨', uri: liffUrl },
                style: 'primary', color: '#b8945a', height: 'sm',
              }],
            },
          },
        }],
        { isMultiple: false }
      )
      if (result?.status === 'success') showToast('ส่งให้เพื่อนแล้ว ✨')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      if (e instanceof Error && e.name === 'AbortError') { /* user cancelled */ return }
      // shareTargetPicker not enabled → tell user
      if (msg.includes('not supported') || msg.includes('permission') || msg.includes('shareTargetPicker')) {
        showToast('กรุณาเปิด Share Target Picker ใน LINE Console ก่อน')
      } else {
        showToast(`LINE: ${msg.slice(0, 60)}`)
      }
    } finally {
      setLineSharing(false)
    }
  }

  const handleDownload = async () => {
    if (!imgUrl) return
    const a = document.createElement('a')
    a.href = imgUrl
    a.download = `horoscope-${data.date ?? 'today'}.png`
    a.click()
  }

  return (
    <>
      {/* Fullscreen image overlay */}
      {fullscreen && imgUrl && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center"
             style={{ background: 'rgba(0,0,0,0.95)' }} onClick={() => setFullscreen(false)}>
          <button className="absolute top-5 right-5 z-10 p-2 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.15)' }} onClick={() => setFullscreen(false)}>
            <X size={24} color="white" />
          </button>
          <img src={imgUrl} style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
        </div>
      )}

      <div className="fixed inset-0 z-50 flex items-end justify-center"
           style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose}>

        {/* Off-screen card for capture */}
        <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', pointerEvents: 'none' }}>
          <HoroscopeShareCard ref={cardRef} data={data} />
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-2xl text-sm font-semibold"
               style={{ background: 'rgba(30,25,16,0.95)', color: '#d4b884', border: '1px solid rgba(184,148,90,0.3)' }}>
            {toast}
          </div>
        )}

        <div className="w-full max-w-md rounded-t-3xl pb-8"
             style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>

          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-surface-3)' }} />
          </div>

          {/* Title */}
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex items-center gap-2">
              <Share2 size={18} style={{ color: 'var(--color-gold-dark)' }} />
              <h3 className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>แชร์ดวงวันนี้</h3>
            </div>
            <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
          </div>

          {/* Preview — tap to fullscreen */}
          <div className="mx-5 mb-5 rounded-2xl overflow-hidden flex items-center justify-center"
               style={{ background: '#16120d', height: '260px', cursor: imgUrl ? 'zoom-in' : 'default' }}
               onClick={() => imgUrl && setFullscreen(true)}>
            {generating ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full animate-spin"
                     style={{ border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#d4b884' }} />
                <span className="text-xs" style={{ color: '#b8b09a' }}>กำลังสร้างภาพ...</span>
              </div>
            ) : imgUrl ? (
              <div className="relative h-full flex items-center justify-center">
                <img src={imgUrl} className="h-full w-auto object-contain" style={{ maxWidth: '100%' }} />
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg text-[10px] font-semibold"
                     style={{ background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.7)' }}>
                  กดเพื่อดูเต็มจอ
                </div>
              </div>
            ) : (
              <span className="text-xs" style={{ color: '#7a7260' }}>ไม่สามารถสร้างภาพได้</span>
            )}
          </div>

          {/* Platform logo buttons row */}
          <div className="px-5 mb-3">
            <p className="text-[10px] text-center mb-3 font-semibold"
               style={{ color: 'var(--color-text-muted)' }}>
              แชร์ไปยัง
            </p>
            <div className="grid grid-cols-3 gap-3">
              {/* Facebook */}
              <button
                onClick={() => { showToast('เลือก Facebook ในเมนูที่เปิด'); handleNativeShare() }}
                disabled={generating || sharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}
              >
                <FbLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>Facebook</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>Story / Post</span>
              </button>

              {/* Instagram */}
              <button
                onClick={() => { showToast('เลือก Instagram ในเมนูที่เปิด'); handleNativeShare() }}
                disabled={generating || sharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}
              >
                <IgLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>Instagram</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>Story / Post</span>
              </button>

              {/* LINE friend picker */}
              <button
                onClick={handleLineShare}
                disabled={generating || lineSharing}
                className="flex flex-col items-center gap-2 py-3 px-2 rounded-2xl transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: 'var(--color-surface-2)' }}
              >
                <LineLogo />
                <span className="text-[11px] font-semibold" style={{ color: 'var(--color-text)' }}>LINE</span>
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>
                  {lineSharing ? 'กำลังส่ง...' : 'เลือกเพื่อน'}
                </span>
              </button>
            </div>
          </div>

          {/* Download */}
          <div className="px-5">
            <button
              onClick={handleDownload}
              disabled={!imgUrl}
              className="w-full py-3 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-40"
              style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}
            >
              <Download size={15} />
              บันทึกรูปลงเครื่อง
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

interface CategoryMeta {
  emoji: string
  gradient: string
  accentColor: string
  label: string
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  career: {
    emoji: '⚜️',
    gradient: 'linear-gradient(135deg, #f5e090 0%, #c49030 50%, #8a6020 100%)',
    accentColor: '#c49030',
    label: 'การงาน',
  },
  finance: {
    emoji: '💎',
    gradient: 'linear-gradient(135deg, #a78bfa 0%, #6d28d9 50%, #4c1d95 100%)',
    accentColor: '#7c3aed',
    label: 'การเงิน',
  },
  love: {
    emoji: '🌹',
    gradient: 'linear-gradient(135deg, #fda4af 0%, #e11d48 50%, #9f1239 100%)',
    accentColor: '#e11d48',
    label: 'ความรัก',
  },
  health: {
    emoji: '🌿',
    gradient: 'linear-gradient(135deg, #86efac 0%, #16a34a 50%, #14532d 100%)',
    accentColor: '#16a34a',
    label: 'สุขภาพ',
  },
}

function CategoryCard({ categoryKey, text }: {
  categoryKey: string; text: string | null | undefined;
}) {
  const meta = CATEGORY_META[categoryKey]
  if (!meta) return null

  return (
    <div className="rounded-2xl overflow-hidden card-amulet">
      <div className="px-4 py-3 flex items-center gap-3"
           style={{ borderBottom: '1px solid var(--color-surface-3)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
             style={{ background: meta.gradient, boxShadow: `0 3px 10px ${meta.accentColor}55` }}>
          {meta.emoji}
        </div>
        <span className="text-sm font-bold flex-1" style={{ color: 'var(--color-text)' }}>
          {meta.label}
        </span>
        <div className="w-2 h-2 rounded-full flex-shrink-0"
             style={{ background: meta.accentColor, opacity: 0.7 }} />
      </div>
      <div className="px-4 py-3">
        {text ? (
          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text)' }}>{text}</p>
        ) : (
          <div className="space-y-2 py-1">
            <div className="h-2.5 rounded" style={{ background: 'var(--color-surface-2)', width: '80%' }} />
            <div className="h-2.5 rounded" style={{ background: 'var(--color-surface-2)', width: '55%' }} />
          </div>
        )}
      </div>
    </div>
  )
}

export default function HoroscopePage() {
  const { lineUid } = useAppStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [showShare, setShowShare] = useState(false)
  const [startLoading, setStartLoading] = useState(false)
  const [showAlreadyModal, setShowAlreadyModal] = useState<string | undefined>(undefined)
  const [birthSaving, setBirthSaving] = useState(false)
  const [birthError, setBirthError] = useState<string | null>(null)

  const QUERY_KEY = ['horoscope', 'today', lineUid]

  const { data, isLoading, isError, refetch } = useQuery<HoroscopeData>({
    queryKey: QUERY_KEY,
    queryFn: () => api.horoscope.today(lineUid!) as Promise<HoroscopeData>,
    enabled: !!lineUid,
    staleTime: 1000 * 30,
  })

  const handleStart = useCallback(async () => {
    if (!lineUid) return
    setStartLoading(true)
    try {
      const result = await api.horoscope.start(lineUid) as HoroscopeData
      // Set cache directly — no refetch needed (causes flash)
      queryClient.setQueryData(QUERY_KEY, result)
      // Sync tickets in profile (non-blocking)
      queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
      queryClient.invalidateQueries({ queryKey: ['user', 'stats', lineUid] })
    } catch (err: unknown) {
      const e = err as Error & { code?: string; nextRefresh?: string }
      if (e.code === 'already_started') {
        setShowAlreadyModal(e.nextRefresh ?? '')
        await refetch()
      } else {
        alert(T.errorGeneral)
      }
    } finally {
      setStartLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineUid, queryClient, refetch])

  const handleSaveBirthDate = useCallback(async (date: string) => {
    if (!lineUid) return
    setBirthSaving(true)
    setBirthError(null)
    try {
      await api.user.setBirthDate(lineUid, date)
      // Force refetch — meta.needs_birth_date should flip to false
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
    } catch (err: unknown) {
      const e = err as Error
      setBirthError(e.message || BD.invalid)
    } finally {
      setBirthSaving(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineUid, queryClient])

  const categories = ['career', 'finance', 'love', 'health']

  const meta = data?.meta ?? { started: false, free_used: false, unlocked_today: [], tickets: 0, needs_birth_date: false }
  const started = meta.started
  const needsBirthDate = meta.needs_birth_date === true

  // Initial loading OR Gemini gen loading
  if (isLoading || startLoading) {
    return (
      <div className="flex flex-col min-h-dvh" style={{ background: 'var(--color-bg)' }}>
        <div className="px-4 pt-6 pb-3">
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>{T.title}</h1>
        </div>
        <FullLoadingScreen />
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-dvh pb-6" style={{ background: 'var(--color-bg)' }}>

      {showShare && data && <ShareImageModal data={data} onClose={() => setShowShare(false)} />}

      {showAlreadyModal !== undefined && (
        <AlreadyStartedModal onClose={() => setShowAlreadyModal(undefined)} nextRefresh={showAlreadyModal} />
      )}

      <div className="px-4 pt-6 pb-3 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>{T.title}</h1>
            {started && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: 'rgba(184,148,90,0.15)', color: 'var(--color-gold-dark)' }}>
                ✨ ฟรีทุกหมวด
              </span>
            )}
          </div>
          {started && data?.meta?.next_refresh && (
            <p className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: 'var(--color-text-soft)' }}>
              <Clock size={10} />
              {T.nextRefresh}: {new Date(data.meta.next_refresh).toLocaleString('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' })} น.
            </p>
          )}
        </div>
        <button onClick={() => navigate('/store')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                style={{ background: 'var(--color-surface-2)' }}>
          <Ticket size={14} style={{ color: 'var(--color-gold-dark)' }} />
          <span className="text-xs font-bold" style={{ color: 'var(--color-text)' }}>{meta.tickets}</span>
        </button>
      </div>

      {isError && (
        <div className="mx-4 p-6 rounded-2xl card-amulet text-center">
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>{T.errorLoad}</p>
          <button onClick={() => refetch()} className="px-6 py-2.5 rounded-xl font-semibold btn-gold">
            {T.retry}
          </button>
        </div>
      )}

      {needsBirthDate && !isError && (
        <BirthDateForm
          onSave={handleSaveBirthDate}
          loading={birthSaving}
          error={birthError}
          initial={meta.birth_date}
        />
      )}

      {!needsBirthDate && !started && !isError && (
        <EmptyState onStart={handleStart} loading={startLoading} />
      )}

      {started && data && (
        <>
          {data?.date && (
            <div className="mx-4 mb-4 text-center">
              <div className="inline-flex flex-col items-center gap-0.5 px-6 py-2.5 rounded-2xl"
                   style={{
                     background: 'var(--color-surface)',
                     border: '1.5px solid var(--color-gold)',
                     boxShadow: '0 2px 12px rgba(184,148,90,0.18)',
                   }}>
                <span className="text-[9px] font-semibold tracking-widest"
                      style={{ color: 'var(--color-text-muted)' }}>
                  ดวงประจำวัน
                </span>
                <span className="text-base font-bold" style={{ color: 'var(--color-gold-dark)' }}>
                  {formatThaiDate(data.date)}
                </span>
              </div>
            </div>
          )}

          <div className="mx-4 mb-4 p-5 rounded-3xl text-center"
               style={{ background: 'var(--gradient-gold)' }}>
            <div className="flex justify-center mb-2">
              <Sparkles size={40} color="#fff" />
            </div>
            <p className="text-white text-xs font-semibold mb-3 opacity-80">{T.scoreTitle}</p>
            <div className="text-5xl font-bold text-white mb-1">
              {data.overall_score}<span className="text-2xl opacity-70">/10</span>
            </div>
            <div className="mx-auto w-48 mt-3">
              <ScoreBar score={data.overall_score ?? 0} />
            </div>
          </div>

          <div className="mx-4 mb-4">
            <p className="text-[10px] font-bold mb-2" style={{ color: 'var(--color-gold-dark)' }}>สีมงคลประจำวัน</p>
            <div className="grid grid-cols-3 gap-2">
              {([
                { label: 'สีมงคล',         value: data.lucky_color,   border: true },
                { label: 'สีเสริมงาน',     value: data.work_color,    border: false },
                { label: 'สีเสริมความรัก', value: data.love_color,    border: false },
                { label: 'สีเสริมการเงิน', value: data.finance_color, border: false },
                { label: 'สีเสริมสุขภาพ',  value: data.health_color,  border: false },
                { label: 'สีกาลกิณี',       value: data.bad_luck_color,border: false },
              ] as { label: string; value?: string; border: boolean }[]).map(({ label, value, border }) => (
                <div key={label} className="p-2.5 rounded-2xl card-amulet flex flex-col items-center gap-1.5"
                     style={border ? { border: '1.5px solid var(--color-gold)' } : {}}>
                  <div className="w-9 h-9 rounded-full border-2 flex-shrink-0"
                       style={{
                         background: getColorHex(value ?? ''),
                         borderColor: label === 'สีกาลกิณี' ? '#e53e3e55' : 'var(--color-surface-3)',
                         boxShadow: `0 2px 8px ${getColorHex(value ?? '')}55`,
                       }} />
                  <span className="text-[9px] font-semibold text-center leading-tight"
                        style={{ color: label === 'สีกาลกิณี' ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                    {label}
                  </span>
                  <span className="text-[10px] font-bold" style={{ color: label === 'สีกาลกิณี' ? 'var(--color-danger)' : 'var(--color-text)' }}>
                    {value ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mx-4 mb-1 text-center">
            <p className="text-[10px] flex items-center justify-center gap-1" style={{ color: 'var(--color-text-soft)' }}>
              <Sparkles size={10} />
              {T.guideNote}
            </p>
          </div>

          <div className="mx-4 flex flex-col gap-3">
            {categories.map(key => (
              <CategoryCard
                key={key}
                categoryKey={key}
                text={(data as unknown as Record<string, string | null | undefined>)[key]}
              />
            ))}
          </div>

          <button onClick={() => setShowShare(true)}
                  className="mx-4 mt-5 py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2"
                  style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            <Share2 size={16} />
            {T.shareToday}
          </button>

          <p className="text-center text-[10px] mt-4 px-6" style={{ color: 'var(--color-text-soft)' }}>
            {T.disclaimer}
          </p>
        </>
      )}
    </div>
  )
}
